// src/pages/VendorPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";
import { useCountry } from "../context/CountryContext";

const VendorPage = () => {
  const { isGhana, config } = useCountry();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    bankName: "",
    accountNumber: "",
    phone: "",
    whatsapp: "",
    rate: "",
    stock: 0,
    paymentLink: "",
  });
  const [balance, setBalance] = useState(0);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  // 🎨 THEME PER COUNTRY
  const theme = isGhana
    ? {
        bgColor: "bg-[#0a0500]",
        cardBg: "bg-[#1a0a00]",
        inputBg: "bg-[#2a0a00]",
        titleColor: "text-yellow-400",
        balanceColor: "text-yellow-400",
        saveBtn: "bg-gradient-to-r from-yellow-500 to-red-600 hover:from-yellow-600 hover:to-red-700",
        buyBtn: "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700",
        ordersBtn: "bg-gradient-to-r from-red-500 to-yellow-500 hover:from-red-600 hover:to-yellow-600",
        currencySymbol: "₵",
        phoneCode: "233",
        flag: "🇬🇭",
        ratePlaceholder: "Rate per coin (₵)",
        paymentLinkPlaceholder: "Hubtel / Flutterwave Payment Link",
      }
    : {
        bgColor: "bg-gray-900",
        cardBg: "bg-gray-800",
        inputBg: "bg-gray-700",
        titleColor: "text-white",
        balanceColor: "text-green-400",
        saveBtn: "bg-blue-600 hover:bg-blue-700",
        buyBtn: "bg-green-600 hover:bg-green-700",
        ordersBtn: "bg-purple-600 hover:bg-purple-700",
        currencySymbol: "₦",
        phoneCode: "234",
        flag: "🇳🇬",
        ratePlaceholder: "Rate per coin (₦)",
        paymentLinkPlaceholder: "Flutterwave/Paystack Payment Link",
      };

  // Fetch vendor details
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await API.get("/dashboard");
        const user = res.data.user || {};
        const vendor = user.vendorDetails || {};

        setForm({
          name: user.name || "",
          bankName: vendor.bankName || "",
          accountNumber: vendor.accountNumber || "",
          phone: vendor.phone || user.phone || "",
          whatsapp: vendor.whatsapp || "",
          rate: vendor.rate || "",
          stock: vendor.stock || 0,
          paymentLink: vendor.paymentLink || "",
        });

        setBalance(res.data.balances?.account || res.data.user?.balance || 0);
      } catch (err) {
        console.error("Fetch vendor details failed:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Normalize WhatsApp number — uses country-aware code
  const normalizeWhatsapp = (input) => {
    if (!input) return "";
    input = input.trim();

    if (input.startsWith("http")) return input;

    let digits = input.replace(/\D/g, "");

    // If starts with 0, replace with country code
    if (digits.startsWith("0")) digits = theme.phoneCode + digits.slice(1);

    return `https://wa.me/${digits}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        whatsapp: normalizeWhatsapp(form.whatsapp),
        country: isGhana ? "GH" : "NG",
      };

      await API.put("/vendors/details", payload);
      setMessage("✅ Vendor details updated successfully");
      setTimeout(() => setMessage(""), 4000);
    } catch (err) {
      console.error("Update failed:", err.response?.data || err.message);
      setMessage("❌ Failed to update details");
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.bgColor} flex items-center justify-center`}>
        <p className="text-center text-gray-400">Loading vendor data...</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.bgColor} text-white p-6`}>

      {/* Country indicator badge */}
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      <h1 className={`text-2xl font-bold mb-1 ${theme.titleColor}`}>Vendor Dashboard</h1>
      <p className="text-xs text-gray-400 mb-6">
        {theme.flag} {config.name} · Manage your selling profile
      </p>

      {message && (
        <p className={`mb-4 ${message.startsWith("✅") ? "text-green-400" : "text-red-400"}`}>
          {message}
        </p>
      )}

      {/* Balance & Stock */}
      <div className={`${theme.cardBg} p-6 rounded-xl shadow-md mb-6`}>
        <h2 className="text-lg font-bold mb-2">Your Balance</h2>
        <p className={`text-2xl font-extrabold ${theme.balanceColor}`}>
          {theme.currencySymbol}{balance.toLocaleString()}
        </p>
        <p className="text-sm text-gray-400 mt-1">📦 Stock: {form.stock} coins</p>
      </div>

      {/* Vendor Details Form */}
      <form onSubmit={handleSubmit} className={`${theme.cardBg} p-6 rounded-xl shadow-md space-y-4`}>
        <input
          type="text"
          name="name"
          placeholder="Display Name"
          value={form.name}
          onChange={handleChange}
          className={`w-full p-3 rounded-lg ${theme.inputBg} text-white`}
        />
        <input
          type="text"
          name="bankName"
          placeholder={isGhana ? "Bank or Mobile Money Name" : "Bank Name"}
          value={form.bankName}
          onChange={handleChange}
          className={`w-full p-3 rounded-lg ${theme.inputBg} text-white`}
        />
        <input
          type="text"
          name="accountNumber"
          placeholder={isGhana ? "Account / MoMo Number" : "Account Number"}
          value={form.accountNumber}
          onChange={handleChange}
          className={`w-full p-3 rounded-lg ${theme.inputBg} text-white`}
        />
        <input
          type="text"
          name="phone"
          placeholder={`Phone Number (+${theme.phoneCode})`}
          value={form.phone}
          onChange={handleChange}
          className={`w-full p-3 rounded-lg ${theme.inputBg} text-white`}
        />
        <input
          type="text"
          name="whatsapp"
          placeholder="WhatsApp number or link"
          value={form.whatsapp || ""}
          onChange={handleChange}
          className={`w-full p-3 rounded-lg ${theme.inputBg} text-white`}
        />

        <input
          type="url"
          name="paymentLink"
          placeholder={theme.paymentLinkPlaceholder}
          value={form.paymentLink}
          onChange={handleChange}
          className={`w-full p-3 rounded-lg ${theme.inputBg} text-white`}
        />

        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
            {theme.currencySymbol}
          </span>
          <input
            type="number"
            name="rate"
            placeholder={theme.ratePlaceholder}
            value={form.rate}
            onChange={handleChange}
            className={`w-full pl-9 pr-4 py-3 rounded-lg ${theme.inputBg} text-white`}
          />
        </div>

        <input
          type="number"
          name="stock"
          placeholder="Stock (coins)"
          value={form.stock}
          onChange={handleChange}
          className={`w-full p-3 rounded-lg ${theme.inputBg} text-white`}
        />

        <button
          type="submit"
          className={`w-full ${theme.saveBtn} p-3 rounded-lg font-semibold text-white transition`}
        >
          Save Changes
        </button>
      </form>

      {/* Vendor Actions */}
      <div className="mt-6 space-y-3">
        <button
          onClick={() => navigate("/vendor/purchase")}
          className={`w-full ${theme.buyBtn} px-6 py-2 rounded-lg font-semibold text-white transition`}
        >
          + Buy Coins from Admin
        </button>

        <button
          onClick={() => navigate("/vendor/orders")}
          className={`w-full ${theme.ordersBtn} px-6 py-2 rounded-lg font-semibold text-white transition`}
        >
          📋 User Purchase Requests
        </button>
      </div>
    </div>
  );
};

export default VendorPage;
